import React, { useEffect, useState } from "react";
import { logix2Api } from "@/api/adminOneClient";

/**
 * CareLogix™ ORACLE — AdminOne · LOGIX-2™ Console (Phase 16).
 * Read-only operational console for the Autonomous Agentic Intelligence Engine.
 * Strictly additive — does not touch any existing AdminOne page.
 */
export default function AdminLogix2() {
  const [cap, setCap] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [computeOut, setComputeOut] = useState(null);
  const [computeBusy, setComputeBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    logix2Api.capabilities()
      .then(({ data }) => { if (mounted) setCap(data); })
      .catch((e) => { if (mounted) setErr(e?.message || "Failed to load LOGIX-2 capabilities."); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const runComputeDemo = async () => {
    setComputeBusy(true);
    try {
      const { data } = await logix2Api.scoreCompute({
        momentum: 82, fundamental: 78, sentiment: 72, catalyst: 88,
        liquidity: 90, volatility: 36, risk: 34, institutional: 80,
      });
      setComputeOut(data);
    } catch (e) {
      setComputeOut({ error: e?.message || "Compute failed." });
    } finally {
      setComputeBusy(false);
    }
  };

  return (
    <div className="admin-page" data-testid="admin-logix2">
      <header className="admin-page__hd">
        <div>
          <h1 className="admin-page__title">LOGIX-2™ Console</h1>
          <p className="admin-page__sub">Autonomous Agentic Intelligence Engine · Powered by Claude · OpenAI · Gemini · DeepSeek</p>
        </div>
        <div className="admin-page__badges">
          <span className="admin-badge is-on">APPROVED</span>
          <span className="admin-badge is-on">ACTIVE</span>
          <span className="admin-badge is-on">LOCKED</span>
          <span className="admin-badge is-on">CONSTITUTIONAL</span>
        </div>
      </header>

      {loading && <div className="admin-card" data-testid="admin-logix2-loading">Loading LOGIX-2 capabilities…</div>}
      {err && <div className="admin-card admin-card--bad" data-testid="admin-logix2-error">{err}</div>}

      {cap && (
        <>
          {/* Status grid */}
          <section className="admin-grid admin-grid--4" data-testid="admin-logix2-status">
            <div className="admin-card">
              <div className="admin-card__label">Engine</div>
              <div className="admin-card__value">{cap.status.engine}</div>
            </div>
            <div className="admin-card">
              <div className="admin-card__label">Default Model</div>
              <div className="admin-card__value">{cap.status.default_model}</div>
            </div>
            <div className="admin-card">
              <div className="admin-card__label">Signal Taxonomy</div>
              <div className="admin-card__value">{cap.status.signal_taxonomy_count} types</div>
            </div>
            <div className="admin-card">
              <div className="admin-card__label">Watchtowers V2 (additive)</div>
              <div className="admin-card__value">+{cap.status.watchtowers_v2_count} = {cap.status.watchtowers_v2_count + 5}</div>
            </div>
          </section>

          {/* Oracle Score V2 weights */}
          <section className="admin-section" data-testid="admin-logix2-weights">
            <h2 className="admin-section__h">Oracle Score™ V2 — Weighted Composition</h2>
            <table className="admin-table">
              <thead><tr><th>Component</th><th>Weight</th><th>Polarity</th></tr></thead>
              <tbody>
                {Object.entries(cap.score_engine.weights).map(([k, v]) => {
                  const negative = (k === "volatility" || k === "risk");
                  return (
                    <tr key={k}>
                      <td>{k.charAt(0).toUpperCase() + k.slice(1)}</td>
                      <td><b>{(v * 100).toFixed(0)}%</b></td>
                      <td>{negative ? <span className="admin-tag admin-tag--warn">Inverted (lower = better)</span> : <span className="admin-tag admin-tag--ok">Positive</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="admin-grid admin-grid--5" style={{ marginTop: 10 }}>
              {cap.score_engine.grades.map((g) => (
                <div key={g.label} className="admin-card admin-card--compact">
                  <div className="admin-card__label">{g.label}</div>
                  <div className="admin-card__value" style={{ fontSize: 18 }}>{g.min}–{g.max}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Multi-model routing */}
          <section className="admin-section" data-testid="admin-logix2-routing">
            <h2 className="admin-section__h">Multi-Model Routing Manifest</h2>
            <table className="admin-table">
              <thead><tr><th>Tier</th><th>Providers</th><th>Rationale</th></tr></thead>
              <tbody>
                {cap.routing.tiers.map((t) => (
                  <tr key={t.tier}>
                    <td><b>{t.tier.toUpperCase()}</b></td>
                    <td>{t.providers.join(" · ")}</td>
                    <td>{t.rationale}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <h3 className="admin-section__h" style={{ marginTop: 14 }}>Domain Optimization</h3>
            <div className="admin-grid admin-grid--3">
              {Object.entries(cap.routing.domain_optimization).map(([d, p]) => (
                <div key={d} className="admin-card admin-card--compact">
                  <div className="admin-card__label">{d}</div>
                  <div className="admin-card__value" style={{ fontSize: 13 }}>{p}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Signal taxonomy */}
          <section className="admin-section" data-testid="admin-logix2-signals">
            <h2 className="admin-section__h">Signal Fabric™ — Extended Taxonomy ({cap.signal_taxonomy.count})</h2>
            <div className="admin-tags-row">
              {cap.signal_taxonomy.types.map((s) => (
                <span key={s.id} className={`admin-tag admin-tag--${s.tone}`} title={`Domain: ${s.domain}`}>
                  {s.label}
                </span>
              ))}
            </div>
          </section>

          {/* Watchtowers V2 additive */}
          <section className="admin-section" data-testid="admin-logix2-watchtowers">
            <h2 className="admin-section__h">Watchtowers V2 — Additive ({cap.watchtowers_v2_additive.count})</h2>
            <div className="admin-grid admin-grid--3">
              {Object.entries(cap.watchtowers_v2_additive.watchtowers).map(([id, w]) => (
                <div key={id} className="admin-card">
                  <div className="admin-card__label">{w.label}</div>
                  <div className="admin-card__value" style={{ fontSize: 13 }}>{w.categories.length} categories</div>
                  <div style={{ fontSize: 11, color: "rgba(212,197,160,.6)", marginTop: 6 }}>
                    {w.categories.slice(0, 6).join(" · ")}
                    {w.categories.length > 6 ? "…" : ""}
                  </div>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 12, color: "rgba(212,197,160,.55)", marginTop: 8 }}>
              {cap.watchtowers_v2_additive.note}
            </p>
          </section>

          {/* Compute demo */}
          <section className="admin-section" data-testid="admin-logix2-compute">
            <h2 className="admin-section__h">Oracle Score™ V2 — Live Compute Demo</h2>
            <button
              type="button"
              className="admin-btn admin-btn--gold"
              onClick={runComputeDemo}
              disabled={computeBusy}
              data-testid="admin-logix2-run-compute"
            >
              {computeBusy ? "Computing…" : "Run Sample Compute"}
            </button>
            {computeOut && !computeOut.error && (
              <div className="admin-card" style={{ marginTop: 10 }}>
                <div className="admin-card__label">Result</div>
                <div className="admin-card__value" style={{ fontSize: 28 }}>
                  {computeOut.oracle_score} <span style={{ fontSize: 14, color: "#e8a020" }}>· {computeOut.grade}</span>
                </div>
                <div style={{ fontSize: 11, color: "rgba(212,197,160,.55)", marginTop: 6 }}>
                  Fingerprint: {computeOut.fingerprint}
                </div>
              </div>
            )}
            {computeOut?.error && <div className="admin-card admin-card--bad">{computeOut.error}</div>}
          </section>

          {/* Phase 17 — Live Adapters + Scheduler control */}
          <Logix2OpsPanel />
        </>
      )}
    </div>
  );
}

// ─── Phase 17 — Live Adapters + Scheduler Ops Panel (additive) ───
function Logix2OpsPanel() {
  const [adapters, setAdapters] = React.useState(null);
  const [scheduler, setScheduler] = React.useState(null);
  const [probe, setProbe] = React.useState(null);
  const [triggerOut, setTriggerOut] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [probeBusy, setProbeBusy] = React.useState(false);

  const refresh = React.useCallback(async () => {
    try {
      const { logix2Api } = await import("@/api/adminOneClient");
      const a = await logix2Api.adaptersStatus();
      const s = await logix2Api.schedulerStatus();
      setAdapters(a.data);
      setScheduler(s.data);
    } catch (e) {
      // best-effort
    }
  }, []);

  React.useEffect(() => { refresh(); const t = setInterval(refresh, 15000); return () => clearInterval(t); }, [refresh]);

  const runProbe = async () => {
    setProbeBusy(true);
    try {
      const { logix2Api } = await import("@/api/adminOneClient");
      const { data } = await logix2Api.adaptersProbe();
      setProbe(data);
    } catch (e) {
      setProbe({ error: e?.message || "probe failed" });
    } finally {
      setProbeBusy(false);
    }
  };

  const runTrigger = async () => {
    setBusy(true);
    try {
      const { logix2Api } = await import("@/api/adminOneClient");
      const { data } = await logix2Api.schedulerTrigger();
      setTriggerOut(data);
      await refresh();
    } catch (e) {
      setTriggerOut({ ok: false, error: e?.response?.data?.detail || e?.message || "trigger failed" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="admin-section" data-testid="admin-logix2-adapters">
        <h2 className="admin-section__h">Live Data Adapters (Phase 17B)</h2>
        {!adapters && <div className="admin-card">Loading…</div>}
        {adapters && (
          <div className="admin-grid admin-grid--4">
            {["polygon", "odds_api", "sec_edgar", "whale_alert"].map((k) => {
              const a = adapters[k] || {};
              return (
                <div key={k} className="admin-card" data-testid={`admin-logix2-adapter-${k}`}>
                  <div className="admin-card__label">{k}</div>
                  <div className="admin-card__value" style={{ fontSize: 18 }}>
                    {a.live ? "🟢 Live" : "🟡 Missing key"}
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(212,197,160,.55)", marginTop: 4 }}>
                    {a.free ? "Free · no key required" : `Env: ${a.required_env}`}
                  </div>
                  <div style={{ fontSize: 10, color: "rgba(212,197,160,.4)", marginTop: 2 }}>
                    Covers: {(a.covers || []).join(" · ")}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="admin-btn" onClick={runProbe} disabled={probeBusy} data-testid="admin-logix2-adapters-probe">
            {probeBusy ? "Probing…" : "🛰 Live probe"}
          </button>
        </div>
        {probe && !probe.error && (
          <table className="admin-table" style={{ marginTop: 10 }}>
            <thead><tr><th>Adapter</th><th>Live</th><th>Items</th><th>Latency</th><th>Error</th></tr></thead>
            <tbody>
              {Object.entries(probe.results || {}).map(([n, v]) => (
                <tr key={n}>
                  <td><b>{n}</b></td>
                  <td>{v.live ? "🟢" : "🔴"}</td>
                  <td>{v.items}</td>
                  <td>{v.latency_ms} ms</td>
                  <td style={{ fontSize: 11, color: "rgba(212,197,160,.65)" }}>{v.error || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="admin-section" data-testid="admin-logix2-scheduler">
        <h2 className="admin-section__h">Daily Brief Scheduler (Phase 17A)</h2>
        {!scheduler && <div className="admin-card">Loading…</div>}
        {scheduler && (
          <div className="admin-grid admin-grid--3">
            <div className="admin-card"><div className="admin-card__label">Status</div><div className="admin-card__value">{scheduler.running ? "🟢 Running" : "🔴 Stopped"}</div></div>
            <div className="admin-card"><div className="admin-card__label">Cron</div><div className="admin-card__value" style={{ fontSize: 16 }}>{String(scheduler.config?.hour).padStart(2,"0")}:{String(scheduler.config?.minute).padStart(2,"0")} {scheduler.config?.tz}</div></div>
            <div className="admin-card">
              <div className="admin-card__label">Next Run</div>
              <div className="admin-card__value" style={{ fontSize: 13 }}>
                {scheduler.jobs?.[0]?.next_run_time ? new Date(scheduler.jobs[0].next_run_time).toLocaleString() : "—"}
              </div>
            </div>
          </div>
        )}
        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="admin-btn admin-btn--gold" onClick={runTrigger} disabled={busy} data-testid="admin-logix2-scheduler-trigger">
            {busy ? "Triggering…" : "▶ Run brief now (AdminOne)"}
          </button>
        </div>
        {triggerOut && (
          <div className={`admin-card ${triggerOut.ok ? "" : "admin-card--bad"}`} style={{ marginTop: 10 }} data-testid="admin-logix2-trigger-result">
            <div className="admin-card__label">Last manual run</div>
            <div className="admin-card__value" style={{ fontSize: 13 }}>
              {triggerOut.ok ? `✓ chyron=${triggerOut.chyron_inserted} · webhook=${triggerOut.webhook_dispatched ? "✓" : "✗"} · persisted=${triggerOut.persisted ? "✓" : "✗"}` : `Failed: ${triggerOut.error}`}
            </div>
            {triggerOut.started_at && (
              <div style={{ fontSize: 11, color: "rgba(212,197,160,.55)", marginTop: 4 }}>
                {new Date(triggerOut.started_at).toLocaleString()} → {triggerOut.finished_at ? new Date(triggerOut.finished_at).toLocaleString() : "…"}
              </div>
            )}
          </div>
        )}
        {scheduler?.last_run && (
          <div className="admin-card" style={{ marginTop: 8 }}>
            <div className="admin-card__label">Last scheduled run</div>
            <div className="admin-card__value" style={{ fontSize: 13 }}>
              {scheduler.last_run.ok ? `✓ chyron=${scheduler.last_run.chyron_inserted} · webhook=${scheduler.last_run.webhook_dispatched ? "✓" : "✗"}` : `Failed: ${scheduler.last_run.error}`}
            </div>
            <div style={{ fontSize: 11, color: "rgba(212,197,160,.55)", marginTop: 4 }}>
              {scheduler.last_run.started_at ? new Date(scheduler.last_run.started_at).toLocaleString() : "—"}
            </div>
          </div>
        )}
      </section>
    </>
  );
}
